"use client";

import type { Media, VideoDubbingDemoBlock } from "@mw/shared/payload-types";
import type { ImageProps } from "next/image";
import type { PreviewPlayerSize } from "@ui/components/core/PreviewPlayer/previewPlayerSizeConfig";

import { Button } from "@ui/components/core/Button";
import { PreviewPlayerButton } from "@ui/components/core/PreviewPlayer/PreviewPlayerButton";
import { PreviewPlayerProgressRing } from "@ui/components/core/PreviewPlayer/PreviewPlayerProgressRing";
import { getAudioUrl, getVideoUrl } from "@utilities/payload";
import clsx from "clsx";
import * as m from "motion/react-m";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { type MouseEvent, useEffect, useRef, useState } from "react";

import { DubbingTrackChevron } from "../_videoDubbing/DubbingTrackChevron";
import {
  EAGER_LANGUAGE_TRACK_PRELOAD_COUNT,
  LANGUAGE_SCROLL_ADJUSTMENT_MEDIA_QUERY,
  LANGUAGE_SCROLL_EDGE_PEEK_BIAS_REM,
  LANGUAGE_SCROLL_EDGE_TRIGGER_SIZE_REM,
  LANGUAGE_SCROLL_FADE_SIZE_REM,
  LANGUAGE_SCROLL_MASK_STYLE,
  VIDEO_BOTTOM_BLUR_STYLE,
  VIDEO_BOTTOM_GRADIENT_STYLE,
  getDubbingLanguageLabel,
} from "../_videoDubbing/constants";
import { useDubbedVideoPlayer } from "../_videoDubbing/useDubbedVideoPlayer";
import RingTransitionFX from "@/app/_ui/effects/RingTransitionFX";
import { getButtonCenterUv } from "@/app/_ui/effects/getButtonCenterUv";

type VideoDubbingDemoControlsLayout = "default" | "centered-play-button";
type VideoDubbingDemoButtonSize = "md-compact" | "lg-compact";

export type VideoDubbingDemoProps = {
  coverImageProps?: ImageProps;
  dubbingTracks: VideoDubbingDemoBlock["dubbingTracks"] | null | undefined;
  video: Media | string | null | undefined;
  controlsLayout?: VideoDubbingDemoControlsLayout;
  buttonSize?: VideoDubbingDemoButtonSize;
};

type DubbingTrackItem = {
  audioSrc: string | undefined;
  isOriginal: boolean;
  key: string;
  label: string;
};

type PlayPauseButtonProps = {
  buttonLabel: string;
  isPlaying: boolean;
  progressAnimationStartedAt: number | null;
  videoCurrentTime: number;
  videoDuration: number;
  onClick: () => void;
  size?: PreviewPlayerSize;
};

type HiddenAudioTracksProps = {
  eagerAudioTrackKeys: Set<string>;
  tracks: DubbingTrackItem[];
  registerAudioElement: (trackKey: string, audioElement: HTMLAudioElement | null) => void;
};

type TrackSelectorProps = {
  activeTrackKey: string;
  isOriginalTrackActive: boolean;
  languageButtonElements: { current: Map<string, HTMLButtonElement> };
  languageScrollerRef: { current: HTMLDivElement | null };
  languageTrackItems: DubbingTrackItem[];
  originalButtonLabel: string;
  selectLanguageLabel: string;
  onLanguageTrackClick: (trackKey: string, buttonElement: HTMLButtonElement) => void;
  onOriginalTrackClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onTrackChevronClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onWarmAudioTrack: (trackKey: string) => void;
  controlsLayout: VideoDubbingDemoControlsLayout;
  buttonSize: VideoDubbingDemoButtonSize;
};

export function VideoDubbingDemo({
  coverImageProps,
  dubbingTracks,
  video,
  controlsLayout = "default",
  buttonSize = "lg-compact",
}: VideoDubbingDemoProps) {
  const t = useTranslations("ClientKeys.Globals");
  const tracks = dubbingTracks ?? [];
  const trackItems: DubbingTrackItem[] = tracks.map((track, index) => {
    const customLabel = track.isOriginal === true ? track.customLabel?.trim() : undefined;
    const languageLabel = getDubbingLanguageLabel(track.locale);

    return {
      audioSrc: getMediaSrc(track.audio, getAudioUrl),
      isOriginal: track.isOriginal === true,
      key: track.id ?? `${track.locale}-${index}`,
      label: customLabel || languageLabel,
    };
  });
  const originalTrackItems = trackItems.filter(track => track.isOriginal);
  const originalTrackItem = originalTrackItems.length === 1 ? originalTrackItems[0] : undefined;
  const originalTrackKey = originalTrackItem?.key ?? "";
  const languageTrackItems = trackItems.filter(track => track.key !== originalTrackKey);
  const [lastLanguageTrackKey, setLastLanguageTrackKey] = useState<string | null>(null);
  const languageScrollerRef = useRef<HTMLDivElement | null>(null);
  const languageButtonElements = useRef(new Map<string, HTMLButtonElement>());
  const [showCoverImage, setShowCoverImage] = useState(true);
  const glxpContainerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<RingTransitionFX | null>(null);
  const [webglActive, setWebglActive] = useState(false);
  const originalLabel = t("original");
  const originalButtonLabel = originalTrackItem?.label ?? originalLabel;
  const selectLanguageLabel = t("select_language");
  const lastSelectableLanguageTrackKey =
    languageTrackItems.find(track => track.key === lastLanguageTrackKey)?.key ?? languageTrackItems[0]?.key ?? null;
  const videoSrc = getMediaSrc(video, getVideoUrl);
  const eagerLanguageTrackKeys = languageTrackItems
    .slice(0, EAGER_LANGUAGE_TRACK_PRELOAD_COUNT)
    .map(track => track.key);
  const eagerAudioTrackKeys = new Set([originalTrackKey, ...eagerLanguageTrackKeys]);
  const {
    activeTrackKey,
    isPlaying,
    progressAnimationStartedAt,
    reducedMotion,
    registerAudioElement,
    selectTrack: selectPlayerTrack,
    togglePlayback,
    videoCurrentTime,
    videoDuration,
    videoEventHandlers,
    videoMotionStyle,
    videoRef,
    warmAudioTrack,
  } = useDubbedVideoPlayer({
    initialTrackKey: originalTrackKey,
    preloadTrackKeys: eagerAudioTrackKeys,
  });
  const buttonLabel = isPlaying ? t("pause") : t("play");
  const isOriginalTrackActive = activeTrackKey === originalTrackKey;

  useEffect(() => {
    if (!videoSrc) return;
    const videoElement = videoRef.current;
    const containerElement = glxpContainerRef.current;
    if (!videoElement || !containerElement) return;

    let player: RingTransitionFX | null = null;
    let cancelled = false;

    const ensureInit = () => {
      if (player || cancelled) return;
      const instance = new RingTransitionFX(containerElement, videoElement, {
        onReady: () => setWebglActive(true),
      });
      if (!instance.isSupported) {
        instance.dispose();
        return;
      }
      player = instance;
      playerRef.current = instance;
      instance.play();
    };

    videoElement.addEventListener("playing", ensureInit);
    if (!videoElement.paused && videoElement.readyState >= 2) ensureInit();

    return () => {
      cancelled = true;
      videoElement.removeEventListener("playing", ensureInit);
      playerRef.current = null;
      setWebglActive(false);
      player?.dispose();
    };
  }, [videoSrc, videoRef]);

  function selectTrack(trackKey: string, button?: HTMLElement | null) {
    if (trackKey !== activeTrackKey) {
      playerRef.current?.triggerTransition(getButtonCenterUv(button, glxpContainerRef.current));
    }
    if (trackKey !== originalTrackKey) setLastLanguageTrackKey(trackKey);

    selectPlayerTrack(trackKey);
  }

  function revealLanguageTrackButton(buttonElement: HTMLButtonElement) {
    const scrollElement = languageScrollerRef.current;
    if (!scrollElement || !isLanguageScrollAdjustmentEnabled()) return;

    const scrollDelta = getLanguageScrollDelta(scrollElement, buttonElement);
    if (scrollDelta === 0) return;

    scrollElement.scrollBy({ behavior: reducedMotion ? "auto" : "smooth", left: scrollDelta });
  }

  function handleOriginalTrackClick(event?: MouseEvent<HTMLButtonElement>) {
    if (isLanguageScrollAdjustmentEnabled()) {
      languageScrollerRef.current?.scrollTo({ behavior: reducedMotion ? "auto" : "smooth", left: 0 });
    }
    if (!originalTrackKey) return;
    selectTrack(originalTrackKey, event?.currentTarget ?? null);
  }

  function handleLanguageTrackClick(trackKey: string, buttonElement: HTMLButtonElement) {
    revealLanguageTrackButton(buttonElement);
    selectTrack(trackKey, buttonElement);
  }

  function handleTrackChevronClick(event?: MouseEvent<HTMLButtonElement>) {
    if (!isOriginalTrackActive) {
      handleOriginalTrackClick(event);
      return;
    }

    if (!lastSelectableLanguageTrackKey) return;

    const buttonElement = languageButtonElements.current.get(lastSelectableLanguageTrackKey);
    if (buttonElement) revealLanguageTrackButton(buttonElement);
    selectTrack(lastSelectableLanguageTrackKey, event?.currentTarget ?? null);
  }

  if (!videoSrc || !originalTrackKey) {
    return null;
  }

  return (
    <div className='tw-relative tw-size-full tw-overflow-hidden tw-bg-white'>
      <m.div className='tw-size-full tw-origin-center tw-transform-gpu' style={videoMotionStyle}>
        <video
          className={clsx("tw-size-full tw-object-cover", webglActive && "tw-invisible")}
          crossOrigin='anonymous'
          muted
          playsInline
          preload='metadata'
          ref={videoRef}
          src={videoSrc}
          {...videoEventHandlers}
          onPlay={event => {
            setShowCoverImage(false);
            videoEventHandlers.onPlay(event);
          }}
        />
      </m.div>
      <div aria-hidden='true' className='tw-pointer-events-none tw-absolute tw-inset-0' ref={glxpContainerRef} />
      {coverImageProps && (
        <Image
          {...coverImageProps}
          alt=''
          aria-hidden='true'
          className={clsx(
            "tw-pointer-events-none tw-object-cover tw-transition-opacity tw-duration-300",
            !showCoverImage && "tw-opacity-0",
          )}
        />
      )}
      <HiddenAudioTracks
        eagerAudioTrackKeys={eagerAudioTrackKeys}
        registerAudioElement={registerAudioElement}
        tracks={trackItems}
      />
      <div
        aria-hidden='true'
        className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-20 tw-backdrop-blur-[2px] md:tw-h-[6.5rem]'
        style={VIDEO_BOTTOM_BLUR_STYLE}
      />
      <div
        aria-hidden='true'
        className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-40 md:tw-h-[13rem]'
        style={VIDEO_BOTTOM_GRADIENT_STYLE}
      />
      {controlsLayout === "centered-play-button" && (
        <div className='tw-absolute tw-z-10 tw-inset-0 tw-pointer-events-none md:tw-flex tw-items-center tw-justify-center tw-hidden'>
          <div className='tw-pointer-events-auto'>
            <PlayPauseButton
              buttonLabel={buttonLabel}
              isPlaying={isPlaying}
              progressAnimationStartedAt={progressAnimationStartedAt}
              videoCurrentTime={videoCurrentTime}
              videoDuration={videoDuration}
              onClick={togglePlayback}
            />
          </div>
        </div>
      )}
      <div
        className={clsx(
          "tw-absolute tw-inset-x-2 tw-bottom-1 tw-z-10 tw-grid tw-grid-cols-[auto_minmax(0,1fr)] tw-items-center md:tw-inset-x-[1.625rem] md:tw-bottom-5 md:tw-gap-5",
          controlsLayout === "default" && "md:tw-grid-cols-[1fr_64%_1fr]",
          controlsLayout === "centered-play-button" && "md:tw-block",
        )}
      >
        <div
          className={clsx(
            controlsLayout === "default" && "tw-contents",
            controlsLayout === "centered-play-button" && "tw-contents md:tw-hidden",
          )}
        >
          <PlayPauseButton
            buttonLabel={buttonLabel}
            isPlaying={isPlaying}
            progressAnimationStartedAt={progressAnimationStartedAt}
            videoCurrentTime={videoCurrentTime}
            videoDuration={videoDuration}
            onClick={togglePlayback}
            size={buttonSize === "lg-compact" ? "md" : "sm"}
          />
        </div>
        <TrackSelector
          activeTrackKey={activeTrackKey}
          isOriginalTrackActive={isOriginalTrackActive}
          languageButtonElements={languageButtonElements}
          languageScrollerRef={languageScrollerRef}
          languageTrackItems={languageTrackItems}
          originalButtonLabel={originalButtonLabel}
          selectLanguageLabel={selectLanguageLabel}
          onLanguageTrackClick={handleLanguageTrackClick}
          onOriginalTrackClick={handleOriginalTrackClick}
          onTrackChevronClick={handleTrackChevronClick}
          onWarmAudioTrack={warmAudioTrack}
          controlsLayout={controlsLayout}
          buttonSize={buttonSize}
        />
        {controlsLayout === "default" && <div aria-hidden='true' className='tw-pointer-events-none max-md:tw-hidden' />}
      </div>
    </div>
  );
}

function PlayPauseButton({
  buttonLabel,
  isPlaying,
  progressAnimationStartedAt,
  videoCurrentTime,
  videoDuration,
  onClick,
  size = "md",
}: PlayPauseButtonProps) {
  return (
    <button
      type='button'
      aria-label={buttonLabel}
      aria-pressed={isPlaying}
      title={buttonLabel}
      className='tw-group/preview-button tw-justify-self-start tw-rounded-full tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-outline-focus'
      onClick={onClick}
    >
      <PreviewPlayerButton isActive={isPlaying} size={size}>
        <PreviewPlayerProgressRing
          currentTime={videoCurrentTime}
          duration={videoDuration}
          isActive={isPlaying}
          progressAnimationStartedAt={progressAnimationStartedAt}
          size={size}
        />
      </PreviewPlayerButton>
    </button>
  );
}

function HiddenAudioTracks({ eagerAudioTrackKeys, registerAudioElement, tracks }: HiddenAudioTracksProps) {
  return (
    <>
      {tracks.map(({ audioSrc, key }) =>
        audioSrc ? (
          <audio
            key={key}
            aria-hidden='true'
            preload={eagerAudioTrackKeys.has(key) ? "auto" : "none"}
            ref={audioElement => registerAudioElement(key, audioElement)}
            src={audioSrc}
          />
        ) : null,
      )}
    </>
  );
}

function TrackSelector({
  activeTrackKey,
  isOriginalTrackActive,
  languageButtonElements,
  languageScrollerRef,
  languageTrackItems,
  originalButtonLabel,
  selectLanguageLabel,
  onLanguageTrackClick,
  onOriginalTrackClick,
  onTrackChevronClick,
  onWarmAudioTrack,
  controlsLayout,
  buttonSize,
}: TrackSelectorProps) {
  return (
    <div
      className={clsx(
        "tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-1.5",
        controlsLayout === "centered-play-button" ? "tw-justify-start md:tw-justify-center" : "tw-justify-start",
      )}
    >
      {controlsLayout === "centered-play-button" && (
        <div className='tw-hidden md:tw-block tw-w-0 tw-flex-auto tw-max-w-4.5' />
      )}
      <Button
        type='button'
        active={isOriginalTrackActive}
        activePill
        aria-label={originalButtonLabel}
        aria-pressed={isOriginalTrackActive}
        title={originalButtonLabel}
        className={clsx(
          "tw-mr-1 tw-max-w-44 tw-shrink-0 tw-overflow-hidden",
          languageTrackItems.length > 0 && "max-md:tw-hidden",
        )}
        size={buttonSize}
        variant='ghost-inverse'
        onClick={onOriginalTrackClick}
      >
        <span className='tw-truncate'>{originalButtonLabel}</span>
      </Button>
      {languageTrackItems.length > 0 && (
        <>
          <DubbingTrackChevron
            isLanguageTrackActive={!isOriginalTrackActive}
            label={isOriginalTrackActive ? selectLanguageLabel : originalButtonLabel}
            className='max-md:tw-hidden'
            onClick={onTrackChevronClick}
          />
          <div
            aria-label={selectLanguageLabel}
            className={clsx(
              "tw-min-w-0 tw-overflow-x-auto tw-py-1 tw-scrollbar-none",
              controlsLayout === "default" && "tw-flex-1",
              controlsLayout === "centered-play-button" && "max-md:tw-flex-1",
            )}
            ref={languageScrollerRef}
            role='group'
            style={LANGUAGE_SCROLL_MASK_STYLE}
          >
            <div className='tw-flex tw-w-max tw-items-center tw-gap-1.5 tw-px-6'>
              <Button
                type='button'
                active={isOriginalTrackActive}
                activePill
                aria-label={originalButtonLabel}
                aria-pressed={isOriginalTrackActive}
                title={originalButtonLabel}
                className='tw-max-w-44 tw-shrink-0 tw-overflow-hidden md:tw-hidden'
                size={buttonSize}
                variant='ghost-inverse'
                onClick={onOriginalTrackClick}
              >
                <span className='tw-truncate'>{originalButtonLabel}</span>
              </Button>
              {languageTrackItems.map(({ key: trackKey, label }) => {
                const isTrackActive = activeTrackKey === trackKey;

                return (
                  <Button
                    key={trackKey}
                    type='button'
                    active={isTrackActive}
                    activePill
                    aria-pressed={isTrackActive}
                    ref={buttonElement => {
                      if (buttonElement) {
                        languageButtonElements.current.set(trackKey, buttonElement);
                        return;
                      }

                      languageButtonElements.current.delete(trackKey);
                    }}
                    size={buttonSize}
                    variant='ghost-inverse'
                    onFocus={() => onWarmAudioTrack(trackKey)}
                    onClick={event => onLanguageTrackClick(trackKey, event.currentTarget)}
                    onPointerDown={() => onWarmAudioTrack(trackKey)}
                    onPointerEnter={() => onWarmAudioTrack(trackKey)}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function getLanguageScrollDelta(scrollElement: HTMLDivElement, buttonElement: HTMLButtonElement) {
  const scrollRect = scrollElement.getBoundingClientRect();
  const buttonRect = buttonElement.getBoundingClientRect();
  const rootFontSize = Number.parseFloat(window.getComputedStyle(scrollElement.ownerDocument.documentElement).fontSize);
  const rootRemSize = Number.isNaN(rootFontSize) ? 0 : rootFontSize;
  const fadeSize = rootRemSize * LANGUAGE_SCROLL_FADE_SIZE_REM;
  const clearWidth = Math.max(scrollRect.width - fadeSize * 2, 0);
  const triggerSize = Math.min(rootRemSize * LANGUAGE_SCROLL_EDGE_TRIGGER_SIZE_REM, clearWidth / 2);
  const settleSize = Math.max(rootRemSize * LANGUAGE_SCROLL_EDGE_PEEK_BIAS_REM, triggerSize + fadeSize);
  const minScrollDelta = -scrollElement.scrollLeft;
  const maxScrollDelta = scrollElement.scrollWidth - scrollElement.clientWidth - scrollElement.scrollLeft;
  const leftFadeEdge = scrollRect.left + fadeSize;
  const rightFadeEdge = scrollRect.right - fadeSize;
  const leftTriggerEdge = leftFadeEdge + triggerSize;
  const rightTriggerEdge = rightFadeEdge - triggerSize;

  if (buttonRect.right > rightTriggerEdge) {
    return Math.min(buttonRect.right - rightFadeEdge + settleSize, maxScrollDelta);
  }

  if (buttonRect.left < leftTriggerEdge) {
    return Math.max(buttonRect.left - leftFadeEdge - settleSize, minScrollDelta);
  }

  return 0;
}

function isLanguageScrollAdjustmentEnabled() {
  return window.matchMedia(LANGUAGE_SCROLL_ADJUSTMENT_MEDIA_QUERY).matches;
}

function getMediaSrc(media: Media | string | null | undefined, getUrl: (media: Media) => string | undefined) {
  if (!media) return undefined;

  return typeof media === "string" ? media : getUrl(media);
}
